import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, TextField, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  FormControl, InputLabel, Select, MenuItem, Menu
} from '@mui/material';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';

const HistoricoTomaMuestras = () => {
  const server = process.env.REACT_APP_API_ROUTE;
  const key = process.env.REACT_APP_API_KEY;

  // Estados principales
  const [loading, setLoading] = useState(false);
  const [muestras, setMuestras] = useState([]);
  const [estadosDisponibles, setEstadosDisponibles] = useState([]);
  
  // Estados para filtros
  const [fechaInicio, setFechaInicio] = useState(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return hoy.toISOString().split('T')[0];
  });
  
  const [fechaFin, setFechaFin] = useState(() => {
    const hoy = new Date();
    hoy.setHours(23, 59, 59, 999);
    return hoy.toISOString().split('T')[0];
  });

  const [filtros, setFiltros] = useState({
    estados: [], // Array de estados seleccionados
    modulo: '',
    colaborador: '',
    responsableSeguimiento: '',
    tipoMuestra: '',
    criterioIncumple: '',
    paciente: '',
    orden: '',
    cubiculo: '',
    sucursal: ''
  });

  // Estados para datos adicionales
  const [colaboradores, setColaboradores] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [tiposMuestra, setTiposMuestra] = useState([]);
  const [sucursales, setSucursales] = useState([]);

  // Estados para modal de historial individual
  const [modalHistorialOpen, setModalHistorialOpen] = useState(false);
  const [historialMuestraSeleccionada, setHistorialMuestraSeleccionada] = useState([]);
  const [datosMuestraHistorial, setDatosMuestraHistorial] = useState(null);

  // Definición de todas las columnas disponibles
  const columnasDisponibles = [
    { id: 'numero', label: '#', enabled: true },
    { id: 'orden', label: 'Orden', enabled: true },
    { id: 'paciente', label: 'Paciente', enabled: true },
    
    { id: 'modulo', label: 'Módulo', enabled: true },
    { id: 'tipoMuestra', label: 'Tipo Muestra', enabled: true },
    { id: 'estadoActual', label: 'Estado Actual', enabled: true },
    { id: 'colaborador', label: 'Responsable', enabled: true },
    { id: 'cubiculo', label: 'Cubículo', enabled: true },
    { id: 'tiempo', label: 'Tiempo (min)', enabled: true },
    { id: 'comentarios', label: 'Comentarios', enabled: true },
    { id: 'acciones', label: 'Acciones', enabled: true },
    // Columnas adicionales ocultas
    { id: 'idMuestra', label: 'ID', enabled: false },
    { id: 'telefono', label: 'Teléfono', enabled: false },
    { id: 'cantidadAnalisis', label: 'Cantidad Análisis', enabled: false },
    { id: 'detalleAnalisis', label: 'Detalle Análisis', enabled: false },
    { id: 'estadoAnterior', label: 'Estado Anterior', enabled: false },
    { id: 'estadoSiguiente', label: 'Estado Siguiente', enabled: false },
    { id: 'estadoFinal', label: 'Estado Final', enabled: false },
    { id: 'responsable', label: 'Responsable seguimiento', enabled: true },
    { id: 'usuarioAnterior', label: 'Responsable acarreo', enabled: false },
    { id: 'sucursal', label: 'Sucursal', enabled: false },
    { id: 'fechaAnterior', label: 'Fecha Anterior', enabled: false },
    { id: 'fechaActual', label: 'Fecha Actual', enabled: false },
    { id: 'fechaSiguiente', label: 'Fecha Siguiente', enabled: false },
    { id: 'criterioIncumple', label: 'Criterio Incumple', enabled: false },
    { id: 'fechaIngreso', label: 'Fecha Ingreso', enabled: false }
  ];

  // Estado para las columnas seleccionadas
  const getColumnasSeleccionadasFromStorage = () => {
    try {
      const saved = localStorage.getItem('historico-columnas-tabla');
      if (saved) {
        const savedColumns = JSON.parse(saved);
        // Filtrar solo columnas que existen y están habilitadas por defecto
        const columnasHabilitadas = columnasDisponibles.filter(col => col.enabled).map(col => col.id);
        // Mantener solo las columnas guardadas que están en la lista de habilitadas
        return savedColumns.filter(id => columnasHabilitadas.includes(id));
      }
      return columnasDisponibles.filter(col => col.enabled).map(col => col.id);
    } catch (error) {
      console.error('Error al leer columnas seleccionadas:', error);
      return columnasDisponibles.filter(col => col.enabled).map(col => col.id);
    }
  };

  const [columnasSeleccionadas, setColumnasSeleccionadas] = useState(() => getColumnasSeleccionadasFromStorage());
  const [anchorElColumnas, setAnchorElColumnas] = useState(null);

  // Función para obtener sucursales
  const fetchSucursales = async () => {
    try {
      const response = await fetch(`${server}/dashboardkanban/dashboard/sucursales`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        }
      });
      if (response.ok) {
        const data = await response.json();
        const lista = data.$values || data;
        setSucursales(lista);
      }
    } catch (error) {
      console.error('Error al obtener sucursales:', error);
    }
  };

  // Efecto para cargar datos iniciales
  useEffect(() => {
    fetchEstados();
    fetchSucursales();
  }, []);

  // Efecto para cargar datos cuando cambien los filtros principales
  useEffect(() => {
    if (filtros.estados.length > 0) {
      fetchMuestras();
    }
  }, [fechaInicio, fechaFin, filtros.estados]);

  // Efecto para agregar automáticamente columnas cuando se selecciona Muestra No conforme o Verificada
  useEffect(() => {
    const nuevasColumnas = [...columnasSeleccionadas];
    let cambio = false;
    
    if (hasMuestraNoConforme()) {
      const columnasNoConforme = ['responsable', 'criterioIncumple'];
      columnasNoConforme.forEach(col => {
        if (!nuevasColumnas.includes(col)) {
          nuevasColumnas.push(col);
          cambio = true;
        }
      });
    }
    
    if (hasVerificada()) {
      const columnasVerificada = ['usuarioAnterior'];
      columnasVerificada.forEach(col => {
        if (!nuevasColumnas.includes(col)) {
          nuevasColumnas.push(col);
          cambio = true;
        }
      });
    }
    
    if (cambio) {
      setColumnasSeleccionadas(nuevasColumnas);
      localStorage.setItem('historico-columnas-tabla', JSON.stringify(nuevasColumnas));
    }
  }, [filtros.estados, estadosDisponibles]);

  // Función para obtener estados disponibles
  const fetchEstados = async () => {
    try {
      const response = await fetch(`${server}/mg_estados_toma_muestras`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const estados = data.$values || data;
        setEstadosDisponibles(estados);
      }
    } catch (error) {
      console.error('Error al obtener estados:', error);
      Swal.fire('Error', 'No se pudieron cargar los estados disponibles', 'error');
    }
  };

  // Función para obtener las muestras del histórico
  const fetchMuestras = async () => {
    if (filtros.estados.length === 0) {
      setMuestras([]);
      return;
    }

    setLoading(true);
    try {
      const fechaInicioStr = new Date(fechaInicio).toISOString();
      const fechaFinStr = new Date(fechaFin + 'T23:59:59').toISOString();

      let todasLasMuestras = [];
      let colaboradoresSet = new Set();
      let modulosSet = new Set();
      let tiposMuestraSet = new Set();
      // sucursales se cargan desde endpoint dedicado

      // Obtener muestras para cada estado seleccionado
      for (const estadoId of filtros.estados) {
        try {
          console.log('🔍 Obteniendo muestras para estado:', estadoId);
          
          const response = await fetch(
            `${server}/dashboardkanban/kanban/detalle-estado/${estadoId}?fechaInicio=${fechaInicioStr}&fechaFin=${fechaFinStr}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'x-api-key': key,
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            console.log('📊 Datos recibidos para estado', estadoId, ':', data);

            // Extraer muestras del $values si existe
            const muestrasArray = data.muestras?.$values || data.muestras || [];
            
            if (data.estado && Array.isArray(muestrasArray) && muestrasArray.length > 0) {
              const muestrasConEstado = muestrasArray.map(m => ({
                ...m,
                historial: m.historial?.$values || m.historial || [],
                historicoCompleto: m.historicoCompleto?.$values || m.historicoCompleto || [],
                analisis: m.analisis?.$values || m.analisis || []
              }));

              todasLasMuestras = todasLasMuestras.concat(muestrasConEstado);

              // Extraer valores únicos para filtros
              muestrasConEstado.forEach(muestra => {
                if (muestra.colaboradorReporte) colaboradoresSet.add(muestra.colaboradorReporte);
                if (muestra.modulo) modulosSet.add(muestra.modulo);
                if (muestra.tipoMuestra) tiposMuestraSet.add(muestra.tipoMuestra);

              });
            }
          } else {
            console.error('Error al obtener datos del estado', estadoId, ':', response.status);
          }
        } catch (error) {
          console.error('Error al obtener muestras del estado', estadoId, ':', error);
        }
      }

      console.log('📋 Total muestras combinadas:', todasLasMuestras.length);
      setMuestras(todasLasMuestras);
      setColaboradores(Array.from(colaboradoresSet).sort());
      setModulos(Array.from(modulosSet).sort());
      setTiposMuestra(Array.from(tiposMuestraSet).sort());


    } catch (error) {
      console.error('Error al obtener muestras:', error);
      Swal.fire('Error', 'No se pudieron cargar las muestras', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para verificar si se seleccionó "Muestra No conforme"
  const hasMuestraNoConforme = () => {
    return filtros.estados.some(estadoId => {
      const estado = estadosDisponibles.find(e => e.id === estadoId);
      return estado && (estado.estado.toLowerCase().includes('muestra no conforme') || estado.estado.toLowerCase().includes('muestras no conforme'));
    });
  };

  // Función para verificar si se seleccionó "Verificada"
  const hasVerificada = () => {
    return filtros.estados.some(estadoId => {
      const estado = estadosDisponibles.find(e => e.id === estadoId);
      return estado && estado.estado.toLowerCase().includes('verificada');
    });
  };

  // Función para filtrar comentarios automáticos - MEJORADA CON TODOS LOS PATRONES
  const filtrarComentarioAutomatico = (comentario) => {
    if (!comentario || comentario === 'N/A' || comentario === '-' || comentario.trim() === '') return '';
    
    const patronesAutomaticos = [
      /cambio de estado de/i,
      /iniciando proceso/i,
      /estado actualizado automáticamente/i,
      /estado inicial creado automáticamente/i,
      /trasladado por:/i,
      /verificado por:/i,
      /observaciones adicionales si aplica/i,
      /cambio de estado automático/i,
      /cambio de estado automático realizado por/i,
      /estado cambiado por el sistema/i,
      /muestra pendiente en recepc/i,
      /^\s*$/,  // Solo espacios en blanco
      /^N\/A$/i,
      /^-$/,
      /^sin comentarios$/i,
      /^cambio automático/i
    ];
    
    const esAutomatico = patronesAutomaticos.some(patron => patron.test(comentario));
    return esAutomatico ? '' : comentario.trim();
  };

  // Función para verificar si una fila del histórico tiene comentario relevante
  const tieneComentarioRelevante = (registro) => {
    const comentarioFiltrado = filtrarComentarioAutomatico(registro.comentario);
    return comentarioFiltrado && comentarioFiltrado.length > 0;
  };

  // Función para aplicar filtros
  const aplicarFiltros = () => {
    return muestras.filter(muestra => {
      return (
        (!filtros.modulo || muestra.modulo?.toLowerCase().includes(filtros.modulo.toLowerCase())) &&
        (!filtros.colaborador || muestra.colaboradorReporte?.toLowerCase().includes(filtros.colaborador.toLowerCase())) &&
        (!filtros.responsableSeguimiento || muestra.accionSiguiente?.responsableSeguimiento?.toLowerCase().includes(filtros.responsableSeguimiento.toLowerCase())) &&
        (!filtros.tipoMuestra || muestra.tipoMuestra?.toLowerCase().includes(filtros.tipoMuestra.toLowerCase())) &&
        (!filtros.criterioIncumple || muestra.criterioIncumple?.toLowerCase().includes(filtros.criterioIncumple.toLowerCase())) &&
        (!filtros.paciente || muestra.nombrePaciente?.toLowerCase().includes(filtros.paciente.toLowerCase())) &&
        (!filtros.orden || muestra.idOrdenTrabajo?.toString().includes(filtros.orden)) &&
        (!filtros.cubiculo || muestra.cubiculo?.toLowerCase().includes(filtros.cubiculo.toLowerCase())) &&
        (!filtros.sucursal || muestra.sucursalAbreviatura?.toLowerCase().includes(filtros.sucursal.toLowerCase()))
      );
    });
  };

  // Función para verificar si una columna está seleccionada
  const isColumnaSeleccionada = (idColumna) => {
    return columnasSeleccionadas.includes(idColumna);
  };

  // Función para manejar cambio en la selección de columnas
  const handleCambioColumna = (idColumna) => {
    const nuevasColumnas = columnasSeleccionadas.includes(idColumna)
      ? columnasSeleccionadas.filter(id => id !== idColumna)
      : [...columnasSeleccionadas, idColumna];
    
    setColumnasSeleccionadas(nuevasColumnas);
    localStorage.setItem('historico-columnas-tabla', JSON.stringify(nuevasColumnas));
  };

  // Función para abrir/cerrar menú de columnas
  const handleAbrirMenuColumnas = (event) => {
    setAnchorElColumnas(event.currentTarget);
  };

  const handleCerrarMenuColumnas = () => {
    setAnchorElColumnas(null);
  };

  // Función para exportar a Excel
  const exportarAExcel = () => {
    const muestrasFiltradas = aplicarFiltros();
    
    const datosExcel = muestrasFiltradas.map((muestra, index) => {
      const fila = {};
      
      if (columnasSeleccionadas.includes('numero')) fila['#'] = index + 1;
      if (columnasSeleccionadas.includes('orden')) fila['Orden'] = muestra.idOrdenTrabajo;
      if (columnasSeleccionadas.includes('paciente')) fila['Paciente'] = muestra.nombrePaciente || 'N/A';
      if (columnasSeleccionadas.includes('modulo')) fila['Módulo'] = muestra.modulo || 'N/A';
      if (columnasSeleccionadas.includes('tipoMuestra')) fila['Tipo Muestra'] = muestra.tipoMuestra || 'N/A';
      if (columnasSeleccionadas.includes('estadoActual')) fila['Estado Actual'] = muestra.accionActual?.estado || muestra.estadoActual || 'N/A';
      if (columnasSeleccionadas.includes('colaborador')) fila['Responsable'] = muestra.colaboradorReporte || 'N/A';
      if (columnasSeleccionadas.includes('cubiculo')) fila['Cubículo'] = muestra.cubiculo || 'N/A';
            if (columnasSeleccionadas.includes('tiempo')) fila['Tiempo (min)'] = muestra.tiempoEnMinutos || 0;
      if (columnasSeleccionadas.includes('comentarios')) {
        const comentario = muestra.comentarioOperador || muestra.accionActual?.comentario || 'N/A';
        fila['Comentarios'] = filtrarComentarioAutomatico(comentario);
      }
      // Columnas adicionales
      if (columnasSeleccionadas.includes('idMuestra')) fila['ID Muestra'] = muestra.id;
      if (columnasSeleccionadas.includes('telefono')) fila['Teléfono'] = muestra.numeroContacto || 'N/A';
      if (columnasSeleccionadas.includes('cantidadAnalisis')) fila['Cantidad Análisis'] = muestra.cantidadAnalisis || 1;
      if (columnasSeleccionadas.includes('detalleAnalisis')) fila['Detalle Análisis'] = muestra.analisisDetalle || 'N/A';
      if (columnasSeleccionadas.includes('estadoAnterior')) fila['Estado Anterior'] = muestra.accionAnterior?.estado || 'N/A';
      if (columnasSeleccionadas.includes('estadoSiguiente')) fila['Estado Siguiente'] = muestra.accionSiguiente?.estado || 'N/A';
      if (columnasSeleccionadas.includes('estadoFinal')) fila['Estado Final'] = muestra.estadoActual || 'N/A';
      if (columnasSeleccionadas.includes('responsable') && hasMuestraNoConforme()) fila['Responsable seguimiento'] = muestra.accionSiguiente?.responsableSeguimiento || 'N/A';
      if (columnasSeleccionadas.includes('usuarioAnterior') && hasVerificada()) fila['Responsable acarreo'] = muestra.accionAnterior?.usuario || 'N/A';
      if (columnasSeleccionadas.includes('sucursal')) fila['Sucursal'] = muestra.sucursal || 'N/A';
      if (columnasSeleccionadas.includes('fechaAnterior')) fila['Fecha Anterior'] = muestra.accionAnterior ? new Date(muestra.accionAnterior.fecha).toLocaleString('es-ES') : 'N/A';
      if (columnasSeleccionadas.includes('fechaActual')) fila['Fecha Actual'] = muestra.accionActual ? new Date(muestra.accionActual.fecha).toLocaleString('es-ES') : 'N/A';
      if (columnasSeleccionadas.includes('fechaSiguiente')) fila['Fecha Siguiente'] = muestra.accionSiguiente ? new Date(muestra.accionSiguiente.fecha).toLocaleString('es-ES') : 'N/A';
      if (columnasSeleccionadas.includes('criterioIncumple') && hasMuestraNoConforme()) fila['Criterio Incumple'] = muestra.criterioIncumple || 'N/A';
      if (columnasSeleccionadas.includes('fechaIngreso')) fila['Fecha Ingreso'] = new Date(muestra.fechaIngreso).toLocaleString('es-ES');
      
      return fila;
    });

    const worksheet = XLSX.utils.json_to_sheet(datosExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Histórico Muestras');
    
    const estadosTexto = filtros.estados.map(id => {
      const estado = estadosDisponibles.find(e => e.id === id);
      return estado?.estado || 'Estado';
    }).join('_');
    
    const fileName = `Historico_Muestras_${estadosTexto}_${fechaInicio}_${fechaFin}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // Función para abrir historial de muestra individual
  const handleAbrirHistorialMuestra = async (muestra) => {
    try {
      setDatosMuestraHistorial(muestra);
      
      let historicoFinal = [];
      
      if (muestra.historial && Array.isArray(muestra.historial) && muestra.historial.length > 0) {
        historicoFinal = muestra.historial.sort((a, b) => {
          const fechaA = new Date(a.fecha_cambio_estado || 0);
          const fechaB = new Date(b.fecha_cambio_estado || 0);
          return fechaA - fechaB;
        });
      } else {
        console.log('🔍 Obteniendo historial desde API para muestra:', muestra.id);
        const response = await fetch(
          `${server}/mg_historico_estados_muestra/pormuestraxorden/${muestra.id}`, 
          {
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': key,
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          let historico = [];
          
          if (data.values?.$values) historico = data.values.$values;
          else if (data.Values?.$values) historico = data.Values.$values;
          else if (data.$values) historico = data.$values;
          else if (Array.isArray(data)) historico = data;

          if (Array.isArray(historico) && historico.length > 0) {
            historicoFinal = historico.sort((a, b) => {
              const fechaA = new Date(a.fecha_cambio_estado || 0);
              const fechaB = new Date(b.fecha_cambio_estado || 0);
              return fechaA - fechaB;
            });
          }
        }
      }
      
      setHistorialMuestraSeleccionada(historicoFinal);
      setModalHistorialOpen(true);
      
    } catch (error) {
      console.error('Error al abrir historial de muestra:', error);
      Swal.fire('Error', 'No se pudo cargar el historial de la muestra', 'error');
    }
  };

  return (
    <div className="right_col" role="main">
      <div className="">
        <div className="page-title">
          <div className="title_left">
            <h3>📊 Histórico de Toma de Muestras</h3>
          </div>
        </div>
        <div className="clearfix"></div>
        
        <Box sx={{ p: 2 }}>
          {/* Panel de descripción */}
          <Paper sx={{ p: 2, mb: 3, backgroundColor: 'white', boxShadow: 1 }}>
            <Typography variant="body1" color="textSecondary">
              Consulta y análisis histórico de estados de toma de muestras con filtros avanzados y exportación a Excel
            </Typography>
          </Paper>

          {/* Panel de filtros principales */}
          <Paper sx={{ p: 3, mb: 3, backgroundColor: 'white', boxShadow: 1 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              🔍 Filtros de Búsqueda
            </Typography>
        
        {/* Filtros de fecha */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            label="Fecha Inicio"
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="Fecha Fin"
            type="date"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
        </Box>

        {/* Selección de estados */}
        <FormControl fullWidth sx={{ mb: 3 }} size="small">
          <InputLabel>Estados a consultar *</InputLabel>
          <Select
            multiple
            value={filtros.estados}
            label="Estados a consultar *"
            onChange={(e) => setFiltros(prev => ({...prev, estados: e.target.value}))}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((estadoId) => {
                  const estado = estadosDisponibles.find(e => e.id === estadoId);
                  return (
                    <Chip key={estadoId} label={estado?.estado || 'Estado'} size="small" />
                  );
                })}
              </Box>
            )}
          >
            {estadosDisponibles.map((estado) => (
              <MenuItem key={estado.id} value={estado.id}>
                {estado.estado}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

          {/* Filtros adicionales */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: 2,
            mb: 2
          }}>
          <TextField
            label="Orden"
            size="small"
            value={filtros.orden}
            onChange={(e) => setFiltros(prev => ({...prev, orden: e.target.value}))}
            placeholder="Buscar por orden..."
          />
          
          <TextField
            label="Paciente"
            size="small"
            value={filtros.paciente}
            onChange={(e) => setFiltros(prev => ({...prev, paciente: e.target.value}))}
            placeholder="Buscar por paciente..."
          />

          <FormControl size="small">
            <InputLabel>Módulo</InputLabel>
            <Select
              value={filtros.modulo}
              label="Módulo"
              onChange={(e) => setFiltros(prev => ({...prev, modulo: e.target.value}))}
            >
              <MenuItem value="">Todos</MenuItem>
              {modulos.map(modulo => (
                <MenuItem key={modulo} value={modulo}>{modulo}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <InputLabel>Responsable</InputLabel>
            <Select
              value={filtros.colaborador}
              label="Responsable"
              onChange={(e) => setFiltros(prev => ({...prev, colaborador: e.target.value}))}
            >
              <MenuItem value="">Todos</MenuItem>
              {colaboradores.map(colaborador => (
                <MenuItem key={colaborador} value={colaborador}>{colaborador}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <InputLabel>Tipo Muestra</InputLabel>
            <Select
              value={filtros.tipoMuestra}
              label="Tipo Muestra"
              onChange={(e) => setFiltros(prev => ({...prev, tipoMuestra: e.target.value}))}
            >
              <MenuItem value="">Todos</MenuItem>
              {tiposMuestra.map(tipo => (
                <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <InputLabel>Cubículo</InputLabel>
            <Select
              value={filtros.cubiculo}
              label="Cubículo"
              onChange={(e) => setFiltros(prev => ({...prev, cubiculo: e.target.value}))}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="TOMA 1">TOMA 1</MenuItem>
              <MenuItem value="TOMA 2">TOMA 2</MenuItem>
              <MenuItem value="TOMA 3">TOMA 3</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small">
            <InputLabel>Sucursal</InputLabel>
            <Select
              value={filtros.sucursal}
              label="Sucursal"
              onChange={(e) => setFiltros(prev => ({...prev, sucursal: e.target.value}))}
            >
              <MenuItem value="">Todas</MenuItem>
              {sucursales.map(suc => (
                <MenuItem key={suc.id} value={suc.abreviatura}>{suc.nombre}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Panel de resultados */}
      <Paper sx={{ p: 3, backgroundColor: 'white', boxShadow: 1 }}>
        {/* Barra de herramientas */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              📋 Resultados
            </Typography>
            <Chip
              label={`${aplicarFiltros().length} de ${muestras.length} muestras`}
              size="small"
              color="primary"
              variant="outlined"
            />
            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="textSecondary">
                  Cargando...
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<i className="fa fa-columns"></i>}
              onClick={handleAbrirMenuColumnas}
            >
              Columnas ({columnasSeleccionadas.length})
            </Button>
            
            <Button
              variant="contained"
              color="success"
              startIcon={<i className="fa fa-file-excel-o"></i>}
              onClick={exportarAExcel}
              disabled={loading || aplicarFiltros().length === 0}
              size="small"
            >
              Exportar a Excel
            </Button>
          </Box>
        </Box>

        {/* Mensaje de instrucciones */}
        {filtros.estados.length === 0 && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 6, 
            backgroundColor: '#f8f9fa', 
            borderRadius: 2, 
            border: '1px dashed #ccc' 
          }}>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
              ⚠️ Selecciona al menos un estado para consultar
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Elige uno o más estados de la lista para ver el histórico de muestras
            </Typography>
          </Box>
        )}

        {/* Tabla de datos */}
        {filtros.estados.length > 0 && (
          <TableContainer component={Paper} sx={{ maxHeight: '70vh', border: '1px solid #e0e0e0' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {isColumnaSeleccionada('numero') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>#</TableCell>}
                  {isColumnaSeleccionada('orden') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Orden</TableCell>}
                  {isColumnaSeleccionada('paciente') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Paciente</TableCell>}
                  {isColumnaSeleccionada('modulo') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Módulo</TableCell>}
                  {isColumnaSeleccionada('tipoMuestra') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Tipo Muestra</TableCell>}
                  {isColumnaSeleccionada('estadoActual') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Estado Actual</TableCell>}
                  {isColumnaSeleccionada('colaborador') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Responsable</TableCell>}
                  {isColumnaSeleccionada('cubiculo') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Cubículo</TableCell>}
                  {isColumnaSeleccionada('tiempo') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Tiempo (min)</TableCell>}
                  {isColumnaSeleccionada('comentarios') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Comentarios</TableCell>}
                  {isColumnaSeleccionada('acciones') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Acciones</TableCell>}
                  {/* Columnas adicionales */}
                  {isColumnaSeleccionada('idMuestra') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>ID</TableCell>}
                  {isColumnaSeleccionada('telefono') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Teléfono</TableCell>}
                  {isColumnaSeleccionada('cantidadAnalisis') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Cant. Análisis</TableCell>}
                  {isColumnaSeleccionada('detalleAnalisis') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Detalle Análisis</TableCell>}
                  {isColumnaSeleccionada('estadoAnterior') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Estado Anterior</TableCell>}
                  {isColumnaSeleccionada('estadoSiguiente') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Estado Siguiente</TableCell>}
                  {isColumnaSeleccionada('estadoFinal') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Estado Final</TableCell>}
                  {isColumnaSeleccionada('responsable') && hasMuestraNoConforme() && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Responsable seguimiento</TableCell>}
                  {isColumnaSeleccionada('usuarioAnterior') && hasVerificada() && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Responsable acarreo</TableCell>}
                  {isColumnaSeleccionada('sucursal') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Sucursal</TableCell>}
                  {isColumnaSeleccionada('fechaAnterior') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Fecha Anterior</TableCell>}
                  {isColumnaSeleccionada('fechaActual') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Fecha Actual</TableCell>}
                  {isColumnaSeleccionada('fechaSiguiente') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Fecha Siguiente</TableCell>}
                  {isColumnaSeleccionada('criterioIncumple') && hasMuestraNoConforme() && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Criterio Incumple</TableCell>}
                  {isColumnaSeleccionada('fechaIngreso') && <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '11px' }}>Fecha Ingreso</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {aplicarFiltros().map((muestra, index) => (
                  <TableRow 
                    key={muestra.id}
                    hover
                    sx={{ 
                      '&:hover': { backgroundColor: '#f8f9fa' },
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }
                    }}
                  >
                    {isColumnaSeleccionada('numero') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        <strong>{index + 1}</strong>
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('orden') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        <strong>{muestra.idOrdenTrabajo}</strong>
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('paciente') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px', maxWidth: '150px' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {muestra.nombrePaciente || 'N/A'}
                        </div>
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('modulo') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        <Chip
                          label={muestra.modulo || 'N/A'}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '8px', height: '20px' }}
                        />
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('tipoMuestra') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        {muestra.tipoMuestra || 'N/A'}
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('estadoActual') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        <Chip
                          label={muestra.accionActual?.estado || muestra.estadoActual || 'N/A'}
                          size="small"
                          color="primary"
                          sx={{ fontSize: '8px', height: '18px' }}
                        />
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('colaborador') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        {muestra.colaboradorReporte || 'N/A'}
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('cubiculo') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        {muestra.cubiculo || 'N/A'}
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('tiempo') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px', textAlign: 'center' }}>
                        <Chip
                          label={`${muestra.tiempoEnMinutos || 0} min`}
                          size="small"
                          variant="outlined"
                          color="info"
                          sx={{ fontSize: '8px', height: '18px' }}
                        />
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('comentarios') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px', maxWidth: '300px' }}>
                        <div style={{ 
                          whiteSpace: 'pre-wrap',
                          wordWrap: 'break-word',
                          lineHeight: 1.4,
                          maxHeight: '100px',
                          overflow: 'auto'
                        }}>
                          {(() => {
                            const comentario = muestra.comentarioOperador || muestra.accionActual?.comentario || 'N/A';
                            const comentarioFiltrado = filtrarComentarioAutomatico(comentario);
                            return comentarioFiltrado || '-';
                          })()}
                        </div>
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('acciones') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleAbrirHistorialMuestra(muestra)}
                          sx={{ fontSize: '8px', padding: '2px 6px' }}
                        >
                          📋 Historial
                        </Button>
                      </TableCell>
                    )}
                    {/* Columnas adicionales */}
                    {isColumnaSeleccionada('idMuestra') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        {muestra.id}
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('telefono') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        {muestra.numeroContacto || 'N/A'}
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('cantidadAnalisis') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px', textAlign: 'center' }}>
                        {muestra.cantidadAnalisis || 1}
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('detalleAnalisis') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px', maxWidth: '150px' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {muestra.analisisDetalle || 'N/A'}
                        </div>
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('estadoAnterior') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        <Chip
                          label={muestra.accionAnterior?.estado || 'N/A'}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ fontSize: '8px', height: '18px' }}
                        />
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('estadoSiguiente') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        <Chip
                          label={muestra.accionSiguiente?.estado || 'N/A'}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ fontSize: '8px', height: '18px' }}
                        />
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('estadoFinal') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        <Chip
                          label={muestra.estadoActual || 'N/A'}
                          size="small"
                          color="info"
                          sx={{ fontSize: '8px', height: '18px' }}
                        />
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('responsable') && hasMuestraNoConforme() && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        {muestra.accionSiguiente?.responsableSeguimiento || 'N/A'}
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('usuarioAnterior') && hasVerificada() && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        {muestra.accionAnterior?.usuario || 'N/A'}
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('sucursal') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        {muestra.sucursal || 'N/A'}
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('fechaAnterior') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        {muestra.accionAnterior ? new Date(muestra.accionAnterior.fecha).toLocaleString('es-ES') : 'N/A'}
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('fechaActual') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        {muestra.accionActual ? new Date(muestra.accionActual.fecha).toLocaleString('es-ES') : 'N/A'}
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('fechaSiguiente') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        {muestra.accionSiguiente ? new Date(muestra.accionSiguiente.fecha).toLocaleString('es-ES') : 'N/A'}
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('criterioIncumple') && hasMuestraNoConforme() && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        {muestra.criterioIncumple || 'N/A'}
                      </TableCell>
                    )}
                    {isColumnaSeleccionada('fechaIngreso') && (
                      <TableCell sx={{ fontSize: '10px', padding: '4px 8px' }}>
                        {new Date(muestra.fechaIngreso).toLocaleString('es-ES')}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {filtros.estados.length > 0 && aplicarFiltros().length === 0 && !loading && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4, 
            backgroundColor: '#f8f9fa', 
            borderRadius: 2, 
            border: '1px dashed #ccc',
            mt: 2
          }}>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
              🔍 No se encontraron muestras
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Intenta modificar los filtros o el rango de fechas
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Menú de selección de columnas */}
      <Menu
        anchorEl={anchorElColumnas}
        open={Boolean(anchorElColumnas)}
        onClose={handleCerrarMenuColumnas}
        PaperProps={{
          sx: { maxHeight: 400, width: 300 }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
            Seleccionar Columnas
          </Typography>
          {columnasDisponibles.map((columna) => (
            <Box 
              key={columna.id}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1,
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f5f5f5' },
                p: 1,
                borderRadius: 1
              }}
              onClick={() => handleCambioColumna(columna.id)}
            >
              <input
                type="checkbox"
                checked={columnasSeleccionadas.includes(columna.id)}
                readOnly
                style={{ marginRight: 8 }}
              />
              <Typography variant="body2">
                {columna.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Menu>

      {/* Modal de historial individual */}
      <Dialog
        open={modalHistorialOpen}
        onClose={() => setModalHistorialOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#1976d2', color: 'white', position: 'relative' }}>
          <Typography variant="h6">
            📋 Histórico de Estados - {datosMuestraHistorial?.tipoMuestra}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Muestra: {datosMuestraHistorial?.tipoMuestra} | Módulo: {datosMuestraHistorial?.modulo}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
            Los comentarios automáticos del sistema se ocultan, solo se muestran comentarios del operador
          </Typography>
          <IconButton 
            onClick={() => setModalHistorialOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
          >
            <i className="fa fa-times"></i>
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ padding: 0 }}>
          <TableContainer sx={{ maxHeight: '60vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 140 }}>Fecha Cambio</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 130 }}>Estado Anterior</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 130 }}>Estado Nuevo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 120 }}>Usuario</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 400 }}>Comentario del Operador</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historialMuestraSeleccionada.length > 0 ? (
                  historialMuestraSeleccionada.map((registro, index) => (
                    <TableRow key={index} sx={{ 
                      '&:hover': { backgroundColor: '#f8f9fa' },
                      '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                    }}>
                      <TableCell sx={{ fontSize: '11px' }}>
                        {new Date(registro.fecha_cambio_estado).toLocaleString('es-ES')}
                      </TableCell>
                      <TableCell sx={{ fontSize: '11px' }}>
                        <Chip
                          label={registro.id_estado_anteriorNavigation?.estado || 'Inicial'}
                          size="small"
                          color="default"
                          variant="outlined"
                          sx={{ fontSize: '8px' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '11px' }}>
                        <Chip
                          label={registro.id_estado_nuevoNavigation?.estado || 'N/A'}
                          size="small"
                          color="primary"
                          sx={{ fontSize: '8px', fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '11px', fontWeight: 500 }}>
                        {registro.cambiado_por || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '11px', maxWidth: '400px' }}>
                        <div style={{ 
                          whiteSpace: 'pre-wrap',
                          wordWrap: 'break-word',
                          lineHeight: 1.6,
                          maxHeight: '150px',
                          overflow: 'auto',
                          padding: '8px 4px',
                          fontStyle: filtrarComentarioAutomatico(registro.comentario) ? 'italic' : 'normal',
                          color: filtrarComentarioAutomatico(registro.comentario) ? '#333' : '#999'
                        }}>
                          {filtrarComentarioAutomatico(registro.comentario) || '-'}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                        No hay historial de cambios de estado para esta muestra.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setModalHistorialOpen(false)} color="secondary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
        </Box>
      </div>
    </div>
  );
};

export default HistoricoTomaMuestras;
